import { DashboardMenuItem } from '@common/types/common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ICONS } from '@navbar/utils';
import { pathToVP } from '@utils/route';
import clsx from 'clsx';
import { PropsWithChildren, SVGProps, createElement, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface INavbarItemProps extends PropsWithChildren {
    className?: string;
    labelProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>;
    iconProps?: SVGProps<SVGSVGElement> & { extraClassName?: string };
    onExpandClick?: () => void;
    showChildren?: boolean;
    hideLabel?: boolean;
    hideExpandIcon?: boolean;
}

const NavbarItem = ({
    className,
    iconProps: iP,
    labelProps,
    onExpandClick,
    showChildren,
    icon,
    label,
    menuChildren,
    children,
    hideLabel,
    hideExpandIcon,
    disabled,
    link,
    extraLabel,
}: INavbarItemProps & DashboardMenuItem) => {
    const { extraClassName, ...iconProps } = iP || {};
    const { isReact, href, absolute, target } = link ?? {};
    const Wrapper = useCallback(
        ({ children, className }: PropsWithChildren<{ className: string }>) => (
            <li className={className}>
                {!menuChildren ? (
                    isReact ? (
                        <Link to={absolute ? href ?? '' : `/app${href ?? ''}`} target={target}>
                            {children}
                        </Link>
                    ) : (
                        <a href={absolute ? href ?? '' : pathToVP(href).toString()} target={target}>
                            {children}
                        </a>
                    )
                ) : (
                    <div onClick={onExpandClick}>{children}</div>
                )}
            </li>
        ),
        [absolute, href, isReact, target, menuChildren, onExpandClick]
    );
    return (
        <Wrapper
            className={clsx(
                'py-[7px] px-[14px]',
                {
                    'hover:bg-water transition-colors': !menuChildren && !disabled,
                    'cursor-pointer': !disabled,
                    'bg-platinum pointer-events-none': disabled,
                },
                className
            )}
        >
            <div
                className={clsx('flex justify-between group', {
                    '!justify-center': hideLabel,
                })}
            >
                <div className="flex justify-between w-full">
                    <div className="flex gap-3 items-center ">
                        {createElement(ICONS[icon], {
                            ...iconProps,
                            className: clsx('w-[20px] h-[14px]', iconProps?.className, extraClassName, {
                                'fill-spanish-gray': disabled,
                                'w-[22px] h-[20px]': hideLabel,
                            }),
                        })}
                        {!hideLabel ? (
                            <span
                                {...labelProps}
                                className={clsx('text-sm', labelProps?.className, { 'text-spanish-gray': disabled })}
                            >
                                {label}
                            </span>
                        ) : null}
                    </div>
                    {extraLabel && (
                        <div className="bg-jasper text-white rounded-full p-[0.25rem_0.5rem] font-bold text-xs">
                            {hideLabel ? extraLabel.collapsed : extraLabel.expanded}
                        </div>
                    )}
                </div>

                {menuChildren && !hideExpandIcon ? (
                    <button disabled={disabled} onClick={onExpandClick}>
                        <ExpandMoreIcon
                            className={clsx('float-right !transition-transform', { '!fill-spanish-gray': disabled })}
                            sx={{ transform: !showChildren ? 'rotate(-90deg)' : 'none' }}
                        />
                    </button>
                ) : null}
            </div>
            {children}
        </Wrapper>
    );
};
export { NavbarItem };
